/* Dependency Imports */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Settings as SettingsIcon, ArrowRight, PersonAdd, Email, AutoGraph } from "@mui/icons-material";
import { useQuery, useLazyQuery, gql } from "@apollo/client";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SendIcon from "@mui/icons-material/Send";
import { addDays, subDays, subHours } from "date-fns";

/* Project Imports */
import StandardTable from "../tables/StandardTable";
import { useSelectUser } from "../../features/auth/authHooks";
import { useAppDispatch } from "../../app/hooks";
import { setProject } from "../../features/project/projectSlice";
import { showSuccessSnackbar, showErrorSnackbar } from "../../features/snackbar/snackbarSlice";
import { IAppointment } from "../../types/project";
import { IProjectAccess } from "../../types/user";
import { convertAllDates, camelToNormal, dashToTitle, downloadExcel } from "../../utils/function";
import { IStep } from "../../types/process";
import { Container, FlexBetween } from "../../commonStyles";

const RDS_LISTINGS_PROJECT_ID = "65bd404201d843802bb37a51";
const RDS_PROJECT_ID = "6339946d6b2a3eadb397bf60";
const EXCLUDED_DASHBOARD_PROJECT_IDS = [RDS_LISTINGS_PROJECT_ID, RDS_PROJECT_ID];

const Dashboard = () => {
  /* Redux */
  const storeDispatch = useAppDispatch();
  const user = useSelectUser();

  const [dashboardData, setDashboardData] = useState<any[]>([]);
  const [appointmentData, setAppointmentData] = useState<IAppointment[]>([]);
  const [reportData, setReportData] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [value, setValue] = useState<number>(0);
  const [steps, setSteps] = useState<IStep[]>([]);
  const [texts, setTexts] = useState([]);
  const [registrants, setRegistrants] = useState<any[]>([]);
  const [registrantCount, setRegistrantCount] = useState<number>(0);
  const [registrantPage, setRegistrantPage] = useState<number>(1);
  const [registrantProjectFilter, setRegistrantProjectFilter] = useState<string>("");
  const [showPersonal, setShowPersonal] = useState<boolean>(false);
  const registrantPerPage = 10;
  const [upcomingSchedules, setUpcomingSchedules] = useState<any[]>([]);

  /* Hooks */
  const navigate = useNavigate();

  const [getReport, { loading: salesReportLoading }] = useLazyQuery(RDSDEBRIEF, {
    onCompleted: (data) => {
      setReportData(data.rdsDebriefReport);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [getAppointments, { loading: salesLoading }] = useLazyQuery(GETAPPOINTMENTS, {
    onCompleted: (data) => {
      setAppointmentData(data.appointmentPagination.items);
      setCount(data.appointmentPagination.count);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const { loading: stepLoading } = useQuery(GETSTEPS, {
    skip: user?.type === "Developer" || user?.type === "Marketing" || !showPersonal,
    variables: {
      user: user?._id,
      page: 1,
      perPage: 5,
      query: {
        completed: null,
        type: "regular",
        currentProcess: true,
        user: user?._id,
        sourceType: "all",
        processType: "all",
        dueDate: "mostRecent",
      },
    },
    onCompleted: (data) => {
      setSteps(data.getSteps.steps);
    },
    onError: (err) => {
      console.log("err");
    },
  });

  const { loading: textLoading } = useQuery(GETTEXTS, {
    skip: user?.type === "Developer" || user?.type === "Marketing" || !showPersonal,
    variables: {
      user: user?._id,
      projects: [],
      page: 1,
      perPage: 5,
      messageType: "Unarchived",
      lastReply: "Registrant",
    },
    onCompleted: (data) => {
      setTexts(data.getTextMessageGroup.texts);
    },
    onError: (err) => {
      console.log("err");
    },
  });

  const { loading } = useQuery(GETDASHBOARDCOUNTS, {
    skip: user?.type !== "Developer" && user?.type !== "Marketing" && user?.type !== "Manager",
    variables: { projects: user?.projectAccess.map((projectAccess) => projectAccess.project._id) },
    onCompleted: (data) => {
      setDashboardData(data.getDashboardCounts);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const dashboardProjectOptions = useMemo(() => {
    return [...(user?.projectAccess || [])]
      .filter((projectAccess: IProjectAccess) => !EXCLUDED_DASHBOARD_PROJECT_IDS.includes(projectAccess.project._id))
      .sort((a: IProjectAccess, b: IProjectAccess) => a.project.name.localeCompare(b.project.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.projectAccess]);

  const { loading: registrantsLoading } = useQuery(GETDASHBOARDREGISTRANTS, {
    skip: (user?.type === "Developer" || user?.type === "Marketing") || showPersonal,
    variables: {
      projects: registrantProjectFilter
        ? [registrantProjectFilter]
        : dashboardProjectOptions.map((projectAccess: IProjectAccess) => projectAccess.project._id),
      page: registrantPage,
      perPage: registrantPerPage,
    },
    onCompleted: (data) => {
      setRegistrants(data.getDashboardRegistrants.items);
      setRegistrantCount(data.getDashboardRegistrants.count);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const { loading: schedulesLoading } = useQuery(GETDASHBOARDSCHEDULES, {
    skip: user?.type !== "Sales" && user?.type !== "Manager",
    variables: {
      filter: { projects: user?.projectAccess.map((projectAccess) => projectAccess.project._id), upcoming: true, public: false },
    },
    onCompleted: (data) => {
      const upcoming = [...data.scheduleMany]
        .filter((schedule: any) => !schedule.public)
        .sort((a: any, b: any) => new Date(a.schedules[0]?.date).valueOf() - new Date(b.schedules[0]?.date).valueOf());
      setUpcomingSchedules(upcoming);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [downloadGlobalReport, { loading: reportLoading }] = useLazyQuery(DOWNLOADREPORT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [getDailySummaryReport, { loading: dailySummaryLoading }] = useLazyQuery(GETDAILYSUMMARYREPORT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const report = data.dailySummaryReport;

      const registrantColumns = [
        { label: "Project", id: "project" },
        { label: "First Name", id: "firstName" },
        { label: "Last Name", id: "lastName" },
        { label: "Email", id: "email" },
        { label: "Phone Number", id: "primaryPhone" },
        { label: "Rating", id: "rating" },
        { label: "Source", id: "source" },
        { label: "Realtor Type", id: "realtorType" },
        { label: "Onsite Rep", id: "onsiteRep" },
        { label: "Online Rep", id: "onlineRep" },
        { label: "Registered / Reregistered", id: "registrationType" },
        { label: "Registration Date", id: "registrationDate" },
      ];
      const registrantData = report.registrants.map((registrant: any) => ({
        project: registrant.project,
        firstName: registrant.firstName,
        lastName: registrant.lastName,
        email: registrant.email,
        primaryPhone: registrant.primaryPhone,
        rating: registrant.rating,
        source: registrant.source,
        realtorType: registrant.realtorType ? camelToNormal(registrant.realtorType) : "",
        onsiteRep: registrant.onsiteRep,
        onlineRep: registrant.onlineRep,
        registrationType: registrant.registrationType,
        registrationDate: convertAllDates(registrant.registrationDate, "PPpp"),
      }));

      const appointmentColumns = [
        { label: "Project", id: "project" },
        { label: "Event", id: "event" },
        { label: "Date Booked", id: "dateBooked" },
        { label: "Appointment Date", id: "appointmentDate" },
        { label: "Sales Rep", id: "salesRep" },
        { label: "Booked By", id: "bookedBy" },
        { label: "Name", id: "appointmentName" },
        { label: "Email", id: "appointmentEmail" },
        { label: "Phone Number", id: "appointmentPhone" },
      ];
      const appointmentData = report.appointments.map((appointment: any) => ({
        project: appointment.project,
        event: appointment.event,
        dateBooked: convertAllDates(appointment.dateBooked, "PPpp"),
        appointmentDate: convertAllDates(appointment.appointmentDate, "PPpp"),
        salesRep: appointment.salesRep,
        bookedBy: appointment.bookedBy,
        appointmentName: appointment.name,
        appointmentEmail: appointment.email,
        appointmentPhone: appointment.phone,
      }));

      const callColumns = [
        { label: "Project / Personal Number", id: "callSource" },
        { label: "From", id: "from" },
        { label: "To", id: "to" },
        { label: "Registrant", id: "callRegistrantName" },
        { label: "Registrant Email", id: "callRegistrantEmail" },
        { label: "Registrant Phone", id: "registrantPhone" },
        { label: "Time", id: "time" },
        { label: "Status", id: "status" },
        { label: "Duration (s)", id: "duration" },
        { label: "Comment", id: "callComment" },
        { label: "Comment Created Date", id: "callCommentDate" },
        { label: "Comment User", id: "callCommentUser" },
        { label: "Appointment Date", id: "callAppointmentDate" },
        { label: "Appointment Booked By", id: "callAppointmentBookedBy" },
        { label: "Event", id: "callEvent" },
      ];
      const callData = report.calls.map((call: any) => ({
        callSource: call.source,
        from: call.from,
        to: call.to,
        callRegistrantName: call.registrant,
        callRegistrantEmail: call.registrantEmail,
        registrantPhone: call.registrantPrimaryPhone || call.registrantPhone,
        time: convertAllDates(call.time, "PPpp"),
        status: call.status ? dashToTitle(call.status) : "",
        duration: call.duration,
        callComment: call.comment,
        callCommentDate: call.commentCreatedDate ? convertAllDates(call.commentCreatedDate, "PPpp") : "",
        callCommentUser: call.commentUser,
        callAppointmentDate: call.appointmentDate ? convertAllDates(call.appointmentDate, "PPpp") : "",
        callAppointmentBookedBy: call.appointmentBookedBy,
        callEvent: call.event,
      }));

      const textColumns = [
        { label: "Project / Personal Number", id: "textSource" },
        { label: "Recipient", id: "recipient" },
        { label: "Time", id: "time" },
        { label: "Status", id: "status" },
        { label: "Message", id: "message" },
      ];
      const textData = report.texts.map((text: any) => ({
        textSource: text.source,
        recipient: text.recipient,
        time: convertAllDates(text.time, "PPpp"),
        status: text.status ? dashToTitle(text.status) : "",
        message: text.message,
      }));

      const commentColumns = [
        { label: "Project", id: "commentProject" },
        { label: "Registrant", id: "registrantName" },
        { label: "Note", id: "note" },
        { label: "Date", id: "date" },
        { label: "Created By", id: "createdBy" },
        { label: "Registrant Phone", id: "commentRegistrantPhone" },
        { label: "Registrant Email", id: "commentRegistrantEmail" },
      ];
      const commentData = report.comments.map((comment: any) => ({
        commentProject: comment.project,
        registrantName: comment.registrantName,
        note: comment.note,
        date: convertAllDates(comment.date, "PPpp"),
        createdBy: comment.createdBy,
        commentRegistrantPhone: comment.registrantPhone,
        commentRegistrantEmail: comment.registrantEmail,
      }));

      const widths: any = {
        project: 25,
        firstName: 15,
        lastName: 15,
        email: 25,
        primaryPhone: 15,
        rating: 10,
        source: 20,
        realtorType: 15,
        onsiteRep: 20,
        onlineRep: 20,
        registrationType: 20,
        registrationDate: 20,
        event: 20,
        dateBooked: 20,
        appointmentDate: 20,
        salesRep: 20,
        bookedBy: 20,
        appointmentName: 20,
        appointmentEmail: 25,
        appointmentPhone: 18,
        callSource: 25,
        textSource: 25,
        from: 20,
        to: 20,
        registrantPhone: 18,
        callRegistrantName: 20,
        callRegistrantEmail: 25,
        callComment: 40,
        callCommentDate: 20,
        callCommentUser: 20,
        callAppointmentDate: 20,
        callAppointmentBookedBy: 20,
        callEvent: 20,
        recipient: 20,
        time: 20,
        status: 15,
        duration: 12,
        message: 40,
        commentProject: 25,
        registrantName: 20,
        note: 40,
        date: 20,
        createdBy: 20,
        commentRegistrantPhone: 18,
        commentRegistrantEmail: 25,
      };

      downloadExcel(
        [registrantData, appointmentData, callData, textData, commentData],
        [registrantColumns, appointmentColumns, callColumns, textColumns, commentColumns],
        [],
        [[widths]],
        ["Registrants", "Appointments", "Calls", "Texts", "Comments"],
        `Daily Summary - ${convertAllDates(new Date(), "PP")}`
      );
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  useEffect(() => {
    storeDispatch(setProject(null));
  }, [storeDispatch]);

  useEffect(() => {
    if (user?.type === "Sales" || user?.type === "Manager") {
      if (value === 0) {
        getAppointments({
          variables: {
            projects: user?.projectAccess
              .filter((project: any) => !EXCLUDED_DASHBOARD_PROJECT_IDS.includes(project.project._id))
              .map((project: any) => project.project._id),
            startDate: new Date(new Date().setHours(0, 0, 0, 0)),
            endDate: new Date(addDays(new Date().setHours(0, 0, 0, 0), 365)),
            perPage: 10,
            page: pageNumber + 1,
            cancelled: false,
            sort: "DATE_ASC",
          },
        });
      } else if (value === 1) {
        getAppointments({
          variables: {
            projects: user?.projectAccess
              .filter((project: any) => !EXCLUDED_DASHBOARD_PROJECT_IDS.includes(project.project._id))
              .map((project: any) => project.project._id),
            startDate: new Date(subDays(new Date().setHours(0, 0, 0, 0), 365)),
            endDate: new Date(new Date().setHours(0, 0, 0, 0)),
            perPage: 10,
            page: pageNumber + 1,
            cancelled: false,
            sort: "DATE_DESC",
          },
        });
      } else if (value === 2) {
        getAppointments({
          variables: {
            projects: user?.projectAccess
              .filter((project: any) => !EXCLUDED_DASHBOARD_PROJECT_IDS.includes(project.project._id))
              .map((project: any) => project.project._id),
            startDate: new Date(subDays(new Date().setHours(0, 0, 0, 0), 30)),
            endDate: new Date(addDays(new Date().setHours(0, 0, 0, 0), 365)),
            perPage: 10,
            page: pageNumber + 1,
            cancelled: true,
            sort: "DATE_DESC",
          },
        });
      }
    }
  }, [value, pageNumber]);

  const downloadTraffic = async () => {
    storeDispatch(showSuccessSnackbar("An email will be sent to you shortly."));
    await downloadGlobalReport({
      variables: {
        dateStart: new Date(subHours(new Date(), 18)),
        dateEnd: new Date(),
        title: 'Daily'
      },
    });
  };

  const downloadDailySummary = () => {
    getDailySummaryReport();
  };

  const appointmentColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: (rowData: IAppointment) => {
          if (!rowData.registrant) {
            return <Box sx={{ fontWeight: 500 }}>{`${rowData.purchaserInfo.firstName} ${rowData.purchaserInfo.lastName}`}</Box>;
          }
          return (
            <Link
              href={`/dashboard/project/${rowData.project?._id}/registrant/${rowData.registrant._id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700, textDecoration: "none", color: "#000" }}
            >
              {rowData.registrant.fullName}
            </Link>
          );
        },
      },
      {
        Header: "Appointment Date",
        accessor: (rowData: IAppointment) => convertAllDates(rowData.date, "PPpp"),
      },
      {
        Header: "Project",
        accessor: (rowData: IAppointment) => (rowData.project ? rowData.project?.name : ""),
      },
      {
        Header: "Location",
        accessor: (rowData: IAppointment) => rowData.location,
      },
      {
        Header: "Confirmed",
        accessor: (rowData: IAppointment) => (rowData.confirmed ? "Yes" : "No"),
      },
      {
        Header: "Cancelled",
        accessor: (rowData: IAppointment) => (rowData.cancelled ? "Yes" : "No"),
      },
    ];
  }, [pageNumber, value]);

  const stepColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: (rowData: any) => {
          return (
            <Link
              href={`/dashboard/project/${rowData._id.project._id}/registrant/${rowData._id.registrant._id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none", color: "#000" }}
            >
              <strong>{rowData._id.name}</strong>
            </Link>
          );
        },
      },
      {
        Header: "Project",
        accessor: (rowData: any) => rowData._id.project.name,
      },
      {
        Header: "Due Date",
        accessor: (rowData: any) => convertAllDates(new Date(rowData._id.dueDate), "PP"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const messagesColumns = useMemo(() => {
    return [
      {
        Header: "Registrant",
        accessor: (rowData: any) => {
          return (
            <Link
              href={`/dashboard/project/${rowData.project._id}/registrant/${rowData._id._id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none", color: "#000" }}
            >
              <strong>{rowData._id.fullName}</strong>
            </Link>
          );
        },
      },
      {
        Header: "Project",
        accessor: (rowData: any) => rowData.project.name,
      },
      {
        Header: "Last Message",
        accessor: (rowData: any) => (rowData.messages.length ? rowData.messages[rowData.messages.length - 1].content : "N/A"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registrantColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: (rowData: any) => {
          return (
            <Link
              href={`/dashboard/project/${rowData.project._id}/registrant/${rowData._id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none", color: "#000" }}
            >
              <strong>{rowData.fullName}</strong>
            </Link>
          );
        },
      },
      {
        Header: "Project",
        accessor: (rowData: any) => rowData.project.name,
      },
      {
        Header: "Email",
        accessor: (rowData: any) => rowData.email,
      },
      {
        Header: "Phone Number",
        accessor: (rowData: any) => rowData.primaryPhone,
      },
      {
        Header: "Sales Rep",
        accessor: (rowData: any) => (rowData.salesRep ? rowData.salesRep.fullName : ""),
      },
      {
        Header: "Realtor Type",
        accessor: (rowData: any) => (rowData.realtorType ? camelToNormal(rowData.realtorType) : ""),
      },
      {
        Header: "Connected",
        accessor: (rowData: any) => {
          const connections = rowData.connections || [];
          if (connections.some((connection: any) => new Date(connection.date) >= subDays(new Date(), 7))) return "Yes";
          if (connections.length) return "Previously Connected";
          return "No";
        },
        Cell: ({ row: { original } }: any) => {
          const connections = original.connections || [];
          const connected = connections.some((connection: any) => new Date(connection.date) >= subDays(new Date(), 7));
          const previouslyConnected = !connected && connections.length > 0;
          const label = connected ? "Yes" : previouslyConnected ? "Previously Connected" : "No";
          const backgroundColor = connected ? "success.main" : previouslyConnected ? "warning.main" : "error.main";
          return (
            <Box sx={{ m: -2, p: 2, textAlign: "center", color: "#fff", backgroundColor }}>
              {label}
            </Box>
          );
        },
      },
      {
        Header: "Registered At",
        accessor: (rowData: any) => convertAllDates(rowData.createdAt, "PPpp"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleColumns = useMemo(() => {
    return [
      {
        Header: "Project",
        accessor: (rowData: any) => rowData.project.name,
      },
      {
        Header: "Event",
        accessor: (rowData: any) => {
          return (
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => navigate("/dashboard/appointments", { state: { tab: 0, scheduleId: rowData._id } })}
            >
              <strong>{rowData.name}</strong>
            </Box>
          );
        },
      },
      {
        Header: "Date",
        accessor: (rowData: any) => {
          const first = rowData.schedules[0]?.date;
          const last = rowData.schedules[rowData.schedules.length - 1]?.date;
          if (!first) return "";
          if (convertAllDates(first, "PP") === convertAllDates(last, "PP")) return convertAllDates(first, "PP");
          return `${convertAllDates(first, "PP")} - ${convertAllDates(last, "PP")}`;
        },
      },
      {
        Header: "Locations",
        accessor: (rowData: any) => (rowData.locations || []).join(", "),
      },
      {
        Header: "Appointments",
        accessor: (rowData: any) => rowData.appointmentCount,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Project",
        accessor: "project", // accessor is the "key" in the data
        Cell: ({ cell: { value }, row: { original } }: any) => {
          if (user?.type !== "Marketing") {
            return (
              <Link href={`/dashboard/project/${original._id}`} sx={{ textDecoration: "none", color: "#000" }}>
                <strong>{value}</strong>
              </Link>
            );
          } else {
            return value;
          }
        },
      },
      {
        Header: "Registrants",
        accessor: "registrants",
        Cell: ({ cell: { value }, row: { original } }: any) => {
          if (user?.type !== "Marketing") {
            return (
              <Link href={`/dashboard/project/${original._id}/list/all?row=15&page=1`} sx={{ textDecoration: "none", color: "#000" }}>
                {value}
              </Link>
            );
          } else return value;
        },
      },
      {
        Header: "Daily Registrants",
        accessor: "newRegistrants",
        Cell: ({ cell: { value }, row: { original } }: any) => {
          if (user?.type !== "Marketing") {
            return (
              <Link
                href={`/dashboard/project/${original._id}/list/all?row=15&page=1&registrantStart=${new Date().setHours(
                  0,
                  0,
                  0,
                  0
                )}&registrantEnd=${new Date().getTime()}`}
                sx={{ textDecoration: "none", color: "#000" }}
              >
                {value}
              </Link>
            );
          } else return value;
        },
      },
      {
        Header: "Steps Overdue",
        accessor: "stepDue",
        Cell: ({ cell: { value }, row: { original } }: any) => {
          if (user?.type !== "Marketing") {
            return (
              <Link href={`/dashboard/project/${original._id}/actions`} sx={{ textDecoration: "none", color: "#000" }}>
                {value}
              </Link>
            );
          } else return value;
        },
      },
      {
        Header: "First Step Due Today",
        accessor: "shortTermDue",
        Cell: ({ cell: { value }, row: { original } }: any) => {
          if (user?.type !== "Marketing") {
            return (
              <Link
                href={`/dashboard/project/${original._id}/list/first-short-term-step-due?row=15&page=1`}
                sx={{ textDecoration: "none", color: "#000" }}
              >
                {value}
              </Link>
            );
          } else return value;
        },
      },
      {
        Header: "Actions",
        Cell: ({ row: { original } }: any) => {
          if (user?.type !== "Developer" && user?.type !== "Marketing") {
            return (
              <Box>
                <Tooltip title="Project Settings">
                  <IconButton
                    size="large"
                    onClick={() => navigate(`/dashboard/project/${original._id}/settings`)}
                    sx={{
                      "& svg": {
                        color: "primary.main",
                        transition: "0.2s",
                        transform: "translateX(0) rotate(0)",
                      },
                      "&:hover, &:focus": {
                        bgcolor: "unset",
                        "& svg:first-of-type": {
                          transform: "translateX(-4px) rotate(-20deg)",
                        },
                        "& svg:last-of-type": {
                          right: 0,
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <SettingsIcon />
                    <ArrowRight sx={{ position: "absolute", right: 4, opacity: 0 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Create Registrant">
                  <IconButton size="large" onClick={() => navigate(`/dashboard/project/${original._id}/settings`, { state: { tab: 4 } })}>
                    <PersonAdd sx={{ color: "primary.main" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          } else if (user?.type === "Developer") {
            return (
              <Button href={`/dashboard/project/${original._id}`} color="primary" variant="contained">
                View Project
              </Button>
            );
          }
        },
      },
    ],
    [navigate, user?.type]
  );

  const reportColumns = React.useMemo(
    () => [
      {
        Header: "User",
        accessor: (rowData: any) => rowData.user,
      },
      {
        Header: "Number of Calls Made",
        accessor: (rowData: any) => rowData.outboundTotal,
      },
      {
        Header: "Average Outbound Call Duration (s)",
        accessor: (rowData: any) => (rowData.averageDuration ? rowData.averageDuration.toFixed(2) : 0),
      },
      {
        Header: "Total Time On Calls (m)",
        accessor: (rowData: any) => ((rowData.outboundTotal * rowData.averageDuration) / 60).toFixed(2),
      },
      {
        Header: "Steps Completed",
        accessor: (rowData: any) => rowData.stepsCompleted,
      },
      {
        Header: "Steps Overdue",
        accessor: (rowData: any) => rowData.stepsOverdue,
      },
      {
        Header: "Notes Created",
        accessor: (rowData: any) => rowData.notes,
      },
      {
        Header: "Appointments Created",
        accessor: (rowData: any) => rowData.appointmentsCreated,
      },
    ],
    []
  );

  /* Functions */

  return (
    <DashboardContainer>
      <Box sx={{ width: "100%", mb: 1.5, textAlign: "right" }}>
        <FlexBetween sx={{ flexDirection: { xs: "column", sm: "row" } }}>
          {user?.type !== "Marketing" ? (
            <Box>
              <FormControl sx={{ width: "100%" }} size="small">
                <InputLabel id="demo-simple-select-project">Project Directory</InputLabel>
                <Select
                  label="Project Directory"
                  sx={{ width: "200px" }}
                  labelId="demo-simple-select-project"
                  id="demo-simple-project"
                  onChange={(e: any) => {
                    navigate(`/dashboard/project/${e.target.value}`);
                  }}
                  size="small"
                  required
                >
                  {[...user?.projectAccess!]
                    .sort((a: any, b: any) => a.project.name.localeCompare(b.project.name))
                    .map((projectAccess: IProjectAccess, index: number) => {
                      return (
                        <MenuItem key={index} value={projectAccess.project._id}>
                          {projectAccess.project.name}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Box>
          ) : null}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              flexDirection: { xs: "column", sm: "row" },
              mt: { xs: 2, sm: 0 },
            }}
          >
            {user?.type === "Manager" ? (
              <Button
                onClick={() => downloadTraffic()}
                color="success"
                variant="contained"
                sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "max-content" } }}
              >
                <SendIcon /> &nbsp;Email Global Report
              </Button>
            ) : null}
            {user?.type === "Manager" || user?.type === "Admin" ? (
              <Button
                onClick={() => downloadDailySummary()}
                color="success"
                variant="contained"
                disabled={dailySummaryLoading}
                sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "max-content" } }}
              >
                {dailySummaryLoading ? <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" /> : <SendIcon />} &nbsp;Daily Summary
              </Button>
            ) : null}
            {user?.type === "Manager" ? (
              <Button
                sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "max-content" } }}
                href="/dashboard/reporting"
                color="primary"
                variant="contained"
              >
                <AssessmentIcon /> &nbsp;General Reporting
              </Button>
            ) : null}
            {user?.type !== "Developer" && user?.type !== "Marketing" ? (
              <>
                <Button
                  sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "max-content" } }}
                  onClick={() =>
                    getReport({ variables: { dateStart: new Date(subDays(new Date(), 7)), dateEnd: new Date(), email: false } })
                  }
                  color="primary"
                  variant="contained"
                >
                  <AutoGraph /> &nbsp;View Sales Stats
                </Button>
                <Button
                  sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "max-content" } }}
                  onClick={() => setShowPersonal((showPersonal) => !showPersonal)}
                  color={showPersonal ? "success" : "primary"}
                  variant="contained"
                >
                  Personal
                </Button>
                <Button sx={{ width: { xs: "100%", sm: "max-content" } }} href="/settings" color="primary" variant="contained">
                  <SettingsIcon /> &nbsp;Settings
                </Button>
              </>
            ) : null}
            {user?.type === "Marketing" || user?.type === "Developer" ? (
              <Button sx={{ width: { xs: "100%", sm: "max-content" } }} href="/email-templates" color="primary" variant="contained">
                <Email /> &nbsp;Email Templates
              </Button>
            ) : null}
          </Box>
        </FlexBetween>
      </Box>
      {user?.type === "Sales" || user?.type === "Manager" || user?.type === "Admin" || salesReportLoading ? (
        salesReportLoading ? (
          <Box sx={{ textAlign: "center", height: "350px" }}>
            <Skeleton height={500} sx={{ transformOrigin: "0 10%" }} />
          </Box>
        ) : (
          reportData.map((data: any) => {
            return (
              <Container sx={{ my: 2 }}>
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography variant="h2">
                    <strong>{data.project} - Last 7 Days</strong>
                  </Typography>
                  <Box>Office Traffic: {data.traffic}</Box>
                  <Box>Sales: {data.sales.length ? data.sales.join(", ") : "None"}</Box>
                  <Box>Presales: {data.presales.length ? data.presales.join(", ") : "None"}</Box>
                </Box>
                <StandardTable data={data.users} loading={salesReportLoading} columns={reportColumns} />
              </Container>
            );
          })
        )
      ) : null}
      {user?.type !== "Developer" && user?.type !== "Marketing" ? (
        showPersonal ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <StandardTable
                title="Your Actions"
                data={steps}
                loading={stepLoading}
                columns={stepColumns}
                buttons={
                  <Button
                    variant="contained"
                    onClick={() => {
                      navigate("/dashboard/actions?row=15&page=1");
                    }}
                  >
                    View All
                  </Button>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StandardTable
                title="Your Texts"
                data={texts}
                loading={textLoading}
                columns={messagesColumns}
                buttons={
                  <Button
                    variant="contained"
                    onClick={() => {
                      navigate("/dashboard/sms", { state: { user: user?._id } });
                    }}
                  >
                    View All
                  </Button>
                }
              />
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ mb: 2 }}>
            <StandardTable
              title="Registrants"
              data={registrants}
              loading={registrantsLoading}
              columns={registrantColumns}
              buttons={
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="registrant-project-filter-label">Project</InputLabel>
                  <Select
                    label="Project"
                    labelId="registrant-project-filter-label"
                    id="registrant-project-filter"
                    size="small"
                    value={registrantProjectFilter}
                    onChange={(e: any) => {
                      setRegistrantPage(1);
                      setRegistrantProjectFilter(e.target.value);
                    }}
                  >
                    <MenuItem value="">All Projects</MenuItem>
                    {dashboardProjectOptions.map((projectAccess: IProjectAccess, index: number) => {
                      return (
                        <MenuItem key={index} value={projectAccess.project._id}>
                          {projectAccess.project.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              }
            />
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button disabled={registrantPage === 1} onClick={() => setRegistrantPage((registrantPage) => registrantPage - 1)}>
                {"<"}
              </Button>
              <span>{registrantPage}</span>
              <Button
                disabled={registrantPage === Math.ceil(registrantCount / registrantPerPage) || !registrantCount}
                onClick={() => setRegistrantPage((registrantPage) => registrantPage + 1)}
              >
                {">"}
              </Button>
            </Box>
          </Box>
        )
      ) : null}
      {user?.type === "Sales" || user?.type === "Manager" ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Tabs
                sx={{ backgroundColor: "#00142a" }}
                variant="scrollable"
                value={value}
                onChange={(e: any, value) => {
                  setPageNumber(0);
                  setValue(value);
                }}
                aria-label="tabs"
              >
                {["Upcoming", "Past", "Cancelled"].map((tab: string, index: number) => {
                  return <Tab key={index} label={tab} />;
                })}
              </Tabs>
              <StandardTable data={appointmentData} loading={salesLoading} columns={appointmentColumns} title="Appointments" />
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button disabled={pageNumber === 0} onClick={() => setPageNumber(pageNumber - 1)}>
                  {"<"}
                </Button>
                <span>{pageNumber + 1}</span>
                <Button disabled={pageNumber + 1 === Math.ceil(count / 10)} onClick={() => setPageNumber(pageNumber + 1)}>
                  {">"}
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StandardTable
              title="Events & Upcoming Schedules"
              data={upcomingSchedules}
              loading={schedulesLoading}
              columns={scheduleColumns}
              buttons={
                <Button variant="contained" onClick={() => navigate("/dashboard/appointments")}>
                  View All
                </Button>
              }
            />
          </Grid>
        </Grid>
      ) : null}
      {user?.type === "Developer" || user?.type === "Marketing" || user?.type === "Manager" ? (
        <StandardTable data={dashboardData} loading={loading || reportLoading} columns={columns} />
      ) : null}
    </DashboardContainer>
  );
};

/* Styles */
export const DashboardContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`;

/* Types */

/* GQL */
const GETDASHBOARDCOUNTS = gql`
  query getDashboardCounts($projects: [MongoID]!) {
    getDashboardCounts(projects: $projects) {
      _id
      project
      registrants
      newRegistrants
      stepDue
      shortTermDue
    }
  }
`;

const GETDAILYSUMMARYREPORT = gql`
  query dailySummaryReport {
    dailySummaryReport {
      registrants {
        project
        firstName
        lastName
        email
        primaryPhone
        rating
        source
        realtorType
        onsiteRep
        onlineRep
        registrationType
        registrationDate
      }
      appointments {
        project
        event
        dateBooked
        appointmentDate
        salesRep
        bookedBy
        name
        email
        phone
      }
      calls {
        source
        from
        to
        registrantPhone
        time
        status
        duration
        registrant
        registrantEmail
        registrantPrimaryPhone
        comment
        commentCreatedDate
        commentUser
        appointmentDate
        appointmentBookedBy
        event
      }
      texts {
        source
        recipient
        time
        status
        message
      }
      comments {
        project
        registrantName
        note
        date
        createdBy
        registrantPhone
        registrantEmail
      }
    }
  }
`;

const GETDASHBOARDSCHEDULES = gql`
  query scheduleMany($filter: FilterFindManyScheduleInput) {
    scheduleMany(filter: $filter, limit: 10000) {
      _id
      name
      locations
      public
      appointmentCount
      schedules {
        date
      }
      project {
        _id
        name
      }
    }
  }
`;

const GETDASHBOARDREGISTRANTS = gql`
  query getDashboardRegistrants($projects: [MongoID]!, $page: Int, $perPage: Int) {
    getDashboardRegistrants(projects: $projects, page: $page, perPage: $perPage) {
      count
      items {
        _id
        fullName
        email
        primaryPhone
        realtorType
        createdAt
        salesRep {
          fullName
        }
        connections {
          date
        }
        project {
          _id
          name
        }
      }
    }
  }
`;

const DOWNLOADREPORT = gql`
  query globalReport($dateStart: Date, $dateEnd: Date, $title: String) {
    globalReport(dateStart: $dateStart, dateEnd: $dateEnd, title: $title)
  }
`;

const RDSDEBRIEF = gql`
  query rdsDebriefReport($dateStart: Date!, $dateEnd: Date!, $email: Boolean!) {
    rdsDebriefReport(dateStart: $dateStart, dateEnd: $dateEnd, email: $email) {
      project
      traffic
      sales
      presales
      users {
        user
        outboundTotal
        averageDuration
        stepsCompleted
        stepsOverdue
        appointmentsCreated
        notes
      }
    }
  }
`;

const GETAPPOINTMENTS = gql`
  query getFilteredAppointments(
    $page: Int
    $perPage: Int
    $projects: [MongoID!]
    $startDate: String
    $endDate: String
    $cancelled: Boolean
    $sort: SortFindManyAppointmentInput
  ) {
    appointmentPagination(
      page: $page
      perPage: $perPage
      filter: {
        projects: $projects
        dateGreaterThanEqual: $startDate
        dateLessThanEqual: $endDate
        cancelled: $cancelled
      }
      sort: $sort
    ) {
      count
      items {
        _id
        project {
          _id
          name
        }
        user {
          firstName
          lastName
        }
        date
        location
        registrant {
          _id
          fullName
        }
        purchaserInfo {
          firstName
          lastName
          email
          primaryPhone
          numberOfGuests
        }
        status
        type
        confirmed
        cancelled
        salesRep {
          _id
          firstName
          lastName
          fullName
          email
        }
        virtual
      }
    }
  }
`;

const GETSTEPS = gql`
  query getSteps($project: MongoID, $user: MongoID, $page: Int!, $perPage: Int!, $query: StepsQueryInput) {
    getSteps(project: $project, user: $user, page: $page, perPage: $perPage, query: $query) {
      count
      steps {
        _id {
          name
          registrant {
            _id
          }
          dueDate
          project {
            _id
            name
          }
        }
      }
    }
  }
`;

const GETTEXTS = gql`
  query getTextMessageGroup(
    $user: MongoID
    $projects: [String]
    $page: Float!
    $perPage: Float!
    $messageType: String
    $lastReply: String
  ) {
    getTextMessageGroup(
      user: $user
      projects: $projects
      page: $page
      perPage: $perPage
      messageType: $messageType
      lastReply: $lastReply
    ) {
      texts {
        _id {
          _id
          fullName
        }
        project {
          _id
          name
        }
        messages {
          content
          direction
          user {
            fullName
          }
          createdAt
          archived
        }
      }
    }
  }
`;

export default Dashboard;
